const Backbone = require('backbone');
const FinishesCollection = require('app/collections/product/finishes');
const OptionGroupsCollection = require('app/collections/product/optiongroups');
const RecommendedOptionsCollection = require('app/collections/product/recommendedoptions');
const Utils = require('views/store/common/utility');
var Product = Backbone.Model.extend({
	initialize: function() {
		var self = this;

		self.optionGroups = new OptionGroupsCollection(self.get('pricedOptionGroups'));
		self.recommendedOptions = new RecommendedOptionsCollection(self.get('recommendedOptions'));

		if (self.hasRequiredOptions()) {
			self.set('isConfigured', false);
		}

		// Create the collection of finishes
		self.finishes = new FinishesCollection(self.get('finishes'));

		if (self.get('useSelectedFinish') || self.finishes.length === 1) {
			// Get the current active finish
			self.activeFinish = self.finishes.getFinishByUniqueId(self.get('selectedFinish').uniqueId);
		}

		if (self.get('useSelectedFinish')) {
			// Mark finish as active
			if (self.activeFinish !== undefined) {
				self.activeFinish.set('selected', true);
			}
		} else if (self.finishes.length === 1) {
			self.finishes.first().set('selected', true);
		}

		if (self.hasPricedOptions()) {
			// Document me
			self.optionGroups.on(
				'optionGroups:updated',
				function() {
					if (self.optionGroups.getConfiguredOptions().length === self.optionGroups.getRequiredGroups().length) {
						self.set('isConfigured', true);
						Backbone.trigger('product:complete', self);
					} else {
						self.set('isConfigured', false);
						Backbone.trigger('product:incomplete', self);
					}

					// Update Price
					self._updatePrice();
				},
				self
			);
		}

		self.listenTo(Backbone, 'quantity:changed', self.setQuantity);
	},

	/**
	 * _updatePrice - Calculates current product price from priced options & current finish,
	 *	 as well as current price per square foot if the product has square footage.
	 */
	_updatePrice: function() {
		let optionsPrice = this.hasPricedOptions() ? this.optionGroups.getSelectedOptionsPrice() : 0,
			selectedFinishPrice,
			totalPrice;
		if (this.finishes.hasSelectedFinish()) {
			const selectedFinish = this.finishes.getSelectedFinish();
			selectedFinishPrice = selectedFinish.get('price');

			const packagePrice = this.getPackaging();
			if (packagePrice) {
				totalPrice = optionsPrice + packagePrice.price;
				this.set('currentPricePerSquareFoot', totalPrice);
				this.set('currentPrice', totalPrice);
				Backbone.trigger('price:updated', this);
			} else if (this.get('squareFeetPerCarton') !== undefined) {
				// Old Square Footage calculation
				totalPrice = optionsPrice + selectedFinishPrice;
				this.set('currentPricePerSquareFoot', totalPrice / this.get('squareFeetPerCarton'));
				this.set('currentPrice', totalPrice);
				Backbone.trigger('price:updated', this);
			} else {
				this.set('currentPrice', optionsPrice + selectedFinishPrice);
				Backbone.trigger('price:updated', this);
			}
		}
	},

	/**
	 * getCurrentPrice - reads Product models current price if available
	 * updates price if hasn't been defined
	 * @return {Number} Current Price as a factor of finish & price options
	 */
	getCurrentPrice: function() {
		if (this.get('currentPrice') === undefined) {
			this._updatePrice();
		}
		return this.get('currentPrice');
	},

	/**
	 * getSquareFeetPerCarton - reads the Product model's square feet per carton
	 *	 if available
	 * @return {any} The number of square feet included in each carton of the product or undefined
	 */
	getSquareFeetPerCarton: function() {
		const packagePrice = this.getPackaging();
		if (packagePrice) {
			return packagePrice.packagingDetails.value;
		}
		return this.get('squareFeetPerCarton');
	},

	/**
	 * getCurrentPricePerSquareFoot - reads Product model's current price per
	 *	 square foot if available, or updates it if it hasn't been defined.
	 * @return {Number} Current Price per square foot as a factor of finish & price options
	 */
	getCurrentPricePerSquareFoot: function() {
		if (this.get('currentPricePerSquareFoot') === undefined) {
			this._updatePrice();
		}
		return this.get('currentPricePerSquareFoot');
	},

	/**
	 * setQuantity - set the quantity
	 */
	setQuantity: function(data) {
		if (data.id === 'main-product-quantity') {
			let quantity = this.hasPricingByUOM() ? this.getQuantityForSquareFootage(data.quantity) : data.quantity;
			this.set('quantity', quantity);
		}
	},

	/**
	 * getQuantity - gets the quantity
	 * @return {Number} Quantity
	 */
	getQuantity: function() {
		if (this.get('quantity') === undefined) {
			return 1;
		}
		return this.get('quantity');
	},

	/**
	 * getQuantityForSquareFootage - gets the needed quantity to cover the passed square feet.
	 * @param {number} squareFeet
	 * @return {Number} Quantity if this product has square footage, otherwise undefined
	 */
	getQuantityForSquareFootage: function(squareFeet) {
		let quantity;

		if (this.hasPricingByUOM()) {
			quantity = Math.ceil(squareFeet / parseInt(this.getSquareFeetPerCarton(), 10));
		}

		return quantity;
	},

	/**
	 * Retrieves packaging method
	 * @returns {object}
	 */
	getPackaging() {
		if (this.hasPricingByUOM()) {
			const selectedFinish = this.finishes.getSelectedFinish();
			if (!this.hasSquareFootage() && selectedFinish) {
				return this.getFinishUOMPricing(selectedFinish.toJSON());
			}
		}
		return null;
	},

	/**
	 * Retrieves finish unit package price if exists, null otherwise.
	 * @param {object} selectedFinish
	 * @returns {*}
	 */
	getFinishUOMPricing(selectedFinish) {
		const pricing = this.getPricingByUOM(selectedFinish);
		return pricing.hasPricingByUOM ? pricing.selectedFinish.finishUnitPackagePrice : null;
	},

	/**
	 * Retrieves pricing by units of measurements for a given finish
	 * @param {object} [finish]
	 * @return {object}
	 */
	getPricingByUOM: function(finish) {
		return Utils.defined(finish) && Utils.defined(finish.finishUnitPackagePrice)
			? { selectedFinish: finish, hasPricingByUOM: this.hasPricingByUOM() }
			: { hasPricingByUOM: false };
	},

	/**
	 * hasSquareFootage - Determine if this is a flooring product
	 * @return {Boolean} Flooring Product or not?
	 */
	hasSquareFootage: function() {
		return this.get('hasSquareFootage');
	},

	/**
	 * Returns true if pricing by size is available, false otherwise.
	 * @returns {boolean}
	 */
	hasPricingByUOM: function() {
		return Utils.defined(this.get('hasPricingByUOM')) && this.get('hasPricingByUOM');
	},

	/**
	 * isSingleFinish - Quick accessor for whether this Product has multiple finishes available
	 * @return {Boolean}
	 */
	isSingleFinish: function() {
		return this.get('finishes').length === 1;
	},

	/**
	 * hasMultipleFinishes - Inverse of isSingleFinish
	 * @return {Boolean}
	 */
	hasMultipleFinishes: function() {
		return !this.isSingleFinish();
	},

	/**
	 * hasPricedOptions - Introspection of whether there are priced options for this product
	 * @return {Boolean} - Value of hasPricedOptions attribute or defaults to `false`
	 */
	hasPricedOptions: function() {
		return this.get('hasPricedOptions');
	},

	hasPricedOptionsExcludingInstall: function() {
		return this.get('hasPricedOptionsExcludingInstall');
	},

	/**
	 * hasAvailabilityByLocation - Are there area restrictions for this product?
	 * @return {Boolean}
	 */
	hasAvailabilityByLocation: function() {
		return this.get('isAvailableByLocation');
	},

	/**
	 * isAvailabilityByLocationSet - Has availability been checked?
	 * @return {Boolean}
	 */
	isAvailabilityByLocationSet: function() {
		return this.get('isAvailabilityByLocationSet');
	},

	/**
	 * isPreConfigured - Preconfiguration are the options (ie. finish selected,
	 *					zip entered and checked if availability by location, required priced options selected on desktop)
	 *					that must be configured	before before proceeding to
	 *					configuration page (ie. priced/recommended options)
	 *
	 * @return {Boolean}
	 */
	isPreConfigured: function() {
		if (!this.finishes.hasSelectedFinish()) {
			return false;
		}
		if (this.hasAvailabilityByLocation() && (!this.isAvailabilityByLocationSet() || !this.finishes.getSelectedFinish().isAvailable())) {
			return false;
		}
		if (window.BCOM.device.isDesktop && !this.isConfigured()) {
			return false;
		}

		return true;
	},

	/**
	 * needsConfiguration - Does product have priced/recommended options?
	 * @return {Boolean}
	 */
	needsConfiguration: function() {
		return this.get('needsConfiguration');
	},

	/**
	 * isOnSale - Wrapper for sale items
	 * @return {Boolean}
	 */
	isOnSale: function() {
		return this.get('onSale');
	},

	/**
	 * hasRecommendedOption - Introspection of whether this product has recommended options
	 * @return {Boolean}
	 */
	hasRecommendedOption: function() {
		return this.get('hasRecommendedOption');
	},

	/**
	 * isLowLead - Wrapper for AB1953 items
	 * @return {Boolean}
	 */
	isLowLead: function() {
		return this.get('isAB1953');
	},

	/**
	 * hasRequiredOptions - Are any of the option groups required?
	 * @return {Boolean}
	 */
	hasRequiredOptions: function() {
		return this.get('hasRequiredOptions');
	},

	/**
	 * isConfigured - Is this Product considered Configured?
	 *   A number of factors are examined:
	 *     - Are there priced options
	 *       - If yes, are any required?
	 *          - If yes, are any of them not configured?
	 *            - If yes, set to false and break, no need to continue
	 *     - Does this product have availability restrictions?
	 *       - If yes, is a Postal Code defined by a cookie?
	 *         - If yes, cool
	 *   The various branches of the logic should set the Product attribute isConfigured
	 *
	 * @return {Boolean} - Result Product's isConfigured attribute
	 */
	isConfigured: function() {
		if (this.hasPricedOptions() && this.hasRequiredOptions()) {
			for (var i = this.optionGroups.getRequiredGroups().length - 1; i >= 0; i--) {
				var optionGroup = this.optionGroups.getRequiredGroups()[i];
				if (optionGroup.isConfigured()) {
					this.set('isConfigured', true);
				} else {
					// As long as there's one false group, the whole status is false
					this.set('isConfigured', false);
					break;
				}
			}
		} else {
			this.set('isConfigured', true);
		}
		return this.get('isConfigured');
	},

	getInstallationData: function() {
		return this.get('pricedOptionGroups').filter((pricedOptionGroup) => pricedOptionGroup.identity === 'GetItInstalled')[0];
	},

	offersInstallation: function() {
		return this.get('offersInstallation');
	}
});

module.exports = Product;
